/** @jsxImportSource @emotion/react */
import { Download } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DataGrid, GridCellParams, GridComparatorFn } from '@mui/x-data-grid'
import { capitalizeFirstLetter } from '@app/utils/strings.utils'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { groupHeader, groupItems } from '../RegionFilter/RegionFilter.styles'
import { useLayerDataFetcher } from '../RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import {
  dataGridContainer,
  filtersContainer,
  fullRiskSummary,
  tableContainer,
} from './FullRiskSummary.styles'
import { fetchFullClientRiskSummary, FullRiskSummaryDataType } from './RiskSummaryFetcher'
import { canExportAtom, userAtom } from '@app/hooks/useUserProfile'
import apiClient from '@app/config/axios.config'
import { pollForReport } from '@app/utils/pollForReport'
import { toast } from '@app/components/ui/Toast/toast'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

export const FullRiskSummary = () => {
  const theme = useTheme()
  const clientName = localStorage.getItem('client_name')
  const canExport = useAtomValue(canExportAtom)
  const layerData = useLayerDataFetcher()
  const [isLoading, setIsLoading] = useState(false)
  const { data: user } = useAtomValue(userAtom)

  const [selectedElementDomain, setSelectedElementDomain] = useState<string>('all')

  const elementNames = useMemo(() => {
    return layerData
      ? Object.entries(layerData.assetDialogData)
          .flatMap(([categoryKey, category]) =>
            selectedElementDomain == 'all' || selectedElementDomain == categoryKey
              ? category.groups.flatMap((group) => group.assets)
              : [],
          )
          .reduce(
            (acc, asset) => {
              acc[asset.type] = asset.display_name
              return acc
            },
            {} as Record<string, string>,
          )
      : {}
  }, [layerData, selectedElementDomain])
  const [hazardLayers, setHazardLayers] = useState<string[]>([])

  const allHazardOptions = useMemo(() => {
    return layerData?.hazardDialogData.hazardDetails
  }, [layerData])
  const allHazardScenarioOptions = useMemo(() => {
    return allHazardOptions?.flatMap((option) => option.scenarios)
  }, [allHazardOptions])

  const [regionsToggle, setRegionsToggle] = useState<'regions' | 'all'>('regions')
  const [valueTypeToggle, setValueTypeToggle] = useState<'metric' | 'replacementCost'>('metric')
  const [showPercent, setShowPercent] = useState(false)

  const [cachedRiskSummaryData, setCachedRiskSummaryData] = useState<{
    [key: string]: FullRiskSummaryDataType
  }>({})
  const [riskSummaryData, setRiskSummaryData] = useState<FullRiskSummaryDataType>({
    elements: [],
    rows: [],
    elementExposureLevels: {},
    columnRanges: [],
    elementUnits: {},
  })

  const abortController = useRef(new AbortController())

  useEffect(() => {
    setCachedRiskSummaryData({})
  }, [clientName])

  useEffect(() => {
    if (!hazardLayers) return
    const cacheKey = `${hazardLayers.toSorted().join(',')},${selectedElementDomain},${showPercent},${valueTypeToggle},${regionsToggle}`
    if (cachedRiskSummaryData[cacheKey]) {
      setRiskSummaryData(cachedRiskSummaryData[cacheKey])
      return
    }
    const newAbortController = new AbortController()
    abortController.current.abort()
    abortController.current = newAbortController
    setIsLoading(true)
    fetchFullClientRiskSummary(
      hazardLayers,
      selectedElementDomain,
      showPercent,
      valueTypeToggle,
      regionsToggle,
      newAbortController,
    )
      .then((data) => {
        if (data === null) {
          return
        }

        setCachedRiskSummaryData((prev) => ({ ...prev, [cacheKey]: data }))
        setRiskSummaryData(data)
        setIsLoading(false)
      })
      .catch((e: Error) => {
        if (e.name == 'CanceledError') return

        toast({
          description: 'Something went wrong. Please try again later.',
          type: 'error',
        })
        setIsLoading(false)
        return
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardLayers, selectedElementDomain, showPercent, valueTypeToggle, regionsToggle])

  const elementDomains = useMemo(
    () =>
      layerData
        ? Object.entries(layerData.assetDialogData).reduce(
            (acc, [categoryKey, category]) => {
              if (
                category.groups.some((g) =>
                  g.assets.some((a) => riskSummaryData.elements.includes(a.type)),
                )
              ) {
                acc[categoryKey] = category.groups.flatMap((g) => g.assets.map((a) => a.type))
              }
              return acc
            },
            {} as { [key: string]: string[] },
          )
        : {},
    [riskSummaryData.elements, layerData],
  )

  const columnGroupingModel = useMemo(
    () =>
      riskSummaryData.elements.map((element) => ({
        groupId: element,
        headerName:
          elementNames[element] +
          (riskSummaryData.elementUnits[element]
            ? ' (' + riskSummaryData.elementUnits[element] + ')'
            : ''),
        children: [
          ...riskSummaryData.elementExposureLevels[element].map((level) => ({
            field: `${element}|${level}`,
          })),
          { field: `${element}|total` },
        ],
      })),
    [
      riskSummaryData.elements,
      elementNames,
      riskSummaryData.elementUnits,
      riskSummaryData.elementExposureLevels,
    ],
  )

  const customSortComparator: GridComparatorFn = (v1, v2, cellParams1, cellParams2) => {
    if (cellParams1.id === 'All') return -1
    if (cellParams2.id === 'All') return 1
    if (v1 === '-') v1 = -Infinity
    if (v2 === '-') v2 = -Infinity
    if (typeof v1 === 'string') v1 = parseFloat(v1)
    if (typeof v2 === 'string') v2 = parseFloat(v2)

    return v1 > v2 ? -1 : 1
  }

  const columns = useMemo(
    () => [
      { field: 'region', headerName: 'Region', width: 150 },
      ...riskSummaryData.elements.flatMap((element) =>
        selectedElementDomain === 'all' || elementDomains[selectedElementDomain]?.includes(element)
          ? [
              ...riskSummaryData.elementExposureLevels[element].map((level, level_i) => ({
                field: `${element}|${level}`,
                headerName: `${capitalizeFirstLetter(level)}${showPercent ? ' (%)' : ''}`,
                sortComparator: customSortComparator,
                type: 'number' as const,
                flex: 1,
                minWidth: 88,
                max: riskSummaryData.columnRanges.find(
                  (range) => range.field === `${element}|${level}`,
                )?.max,
                isFirstInGroup: level_i === 0,
                cellClassName: (params: GridCellParams) => {
                  let classes = ''
                  if ((params.colDef as unknown as { isFirstInGroup: boolean }).isFirstInGroup)
                    classes += 'first-in-group'
                  return classes
                },
              })),
              {
                field: `${element}|total`,
                headerName: 'Total',
                minWidth: 88,
                flex: 1,
                sortComparator: customSortComparator,
                type: 'number' as const,
                max: riskSummaryData.columnRanges.find(
                  (range) => range.field === `${element}|total`,
                )?.max,
              },
            ]
          : [],
      ),
    ],
    [
      riskSummaryData.elements,
      selectedElementDomain,
      elementDomains,
      riskSummaryData.elementExposureLevels,
      riskSummaryData.columnRanges,
      showPercent,
    ],
  )

  const downloadCSV = () => {
    try {
      toast({
        description:
          'Generating data for report. This may take a while, we will notify you when the report is ready.',
        type: 'info',
      })

      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'full-risk-summary',
        format: 'csv',
      })

      const report_id = `${user?.user_uuid}-${Date.now()}`

      apiClient.post(`/report/export/${clientName}/full-risk-summary-csv/${report_id}`, {
        hazardLayers,
        regionsToggle,
        selectedElementDomain,
        valueTypeToggle,
        showPercent,
      })

      pollForReport(report_id, 'risk_summary.csv')
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  return (
    <Box css={fullRiskSummary({ theme })}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          color="#0b2948"
          fontSize="16px"
          marginBottom="16px"
          marginTop="8px"
          fontWeight="600"
        >
          Summary of Asset Risk to Regions
        </Typography>
        {riskSummaryData.elements.length > 0 && !isLoading && canExport && (
          <Button
            variant="outlined"
            sx={{
              color: '#0b2948',
              borderColor: '#d4d9de',
              padding: '4px 16px 4px 8px',
              gap: '4px',
              '&:hover': {
                borderColor: '#d4d9de',
                backgroundColor: '#f0f3f4',
              },
            }}
            size="small"
            onClick={downloadCSV}
          >
            <Download sx={{ height: '18px' }} />
            Download CSV
          </Button>
        )}
      </Box>
      <Autocomplete
        sx={{ width: '100%', minWidth: '240px' }}
        value={
          allHazardScenarioOptions?.filter((option) => hazardLayers.includes(option.assetTag)) ?? []
        }
        isOptionEqualToValue={(option, value) => option.assetTag === value.assetTag}
        size="small"
        fullWidth
        multiple
        disableCloseOnSelect
        limitTags={2}
        options={allHazardScenarioOptions ?? []}
        groupBy={(option) => option.hazard_name ?? ''}
        getOptionLabel={(option) => option.display_name}
        getOptionKey={(option) => option.assetTag}
        onChange={(_event, values) => {
          setHazardLayers(values.map((value) => value.assetTag))
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            key="hazard-layer-filter"
            placeholder={hazardLayers.length == 0 ? 'Choose hazard scenarios' : ''}
          />
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <div css={groupHeader({ theme })}>{params.group}</div>
            <ul css={groupItems({ theme })}>{params.children}</ul>
          </li>
        )}
      />
      <Box css={filtersContainer({ theme })}>
        <FormGroup row>
          <Typography>Element group:</Typography>
          <Select
            value={selectedElementDomain}
            onChange={(event) => {
              setSelectedElementDomain(event.target.value as string)
            }}
          >
            <MenuItem value={'all'}>All</MenuItem>
            {Object.keys(elementDomains)
              .toSorted()
              .map((categoryKey) => (
                <MenuItem key={categoryKey} value={categoryKey}>
                  {categoryKey}
                </MenuItem>
              ))}
          </Select>
        </FormGroup>
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch checked={showPercent} onChange={() => setShowPercent((prev) => !prev)} />
            }
            label="Percent"
          />
        </FormGroup>
        <FormGroup row>
          <Typography>Show:</Typography>
          <ToggleButtonGroup
            value={valueTypeToggle}
            exclusive
            size="small"
            onChange={(_event, value) => {
              if (value) setValueTypeToggle(value)
            }}
          >
            <ToggleButton key={'metric'} value={'metric'}>
              Quantity
            </ToggleButton>
            <ToggleButton key={'replacementCost'} value={'replacementCost'}>
              Value (where available)
            </ToggleButton>
          </ToggleButtonGroup>
        </FormGroup>
        <FormGroup row>
          <Typography>Show exposure:</Typography>
          <ToggleButtonGroup
            value={regionsToggle}
            exclusive
            size="small"
            onChange={(_event, value) => {
              if (value) setRegionsToggle(value)
            }}
          >
            <ToggleButton key={'regions'} value={'regions'}>
              Per Region
            </ToggleButton>
            <ToggleButton key={'all'} value={'all'}>
              For All Regions
            </ToggleButton>
          </ToggleButtonGroup>
        </FormGroup>
      </Box>
      <Box css={tableContainer({ theme })}>
        {riskSummaryData.elements.length > 0 || isLoading ? (
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <DataGrid
              columnGroupingModel={columnGroupingModel}
              rows={riskSummaryData.rows}
              columns={columns}
              loading={isLoading}
              css={dataGridContainer({ theme })}
              rowHeight={32}
              columnHeaderHeight={32}
              disableColumnResize={false}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8',
              borderRadius: '4px',
              color: '#888',
            }}
          >
            Choose hazard scenarios above to view risk to all elements
          </Box>
        )}
      </Box>
    </Box>
  )
}
